import { CompType, expected, randomUUID } from '@aloft/toolkit-shared';
import { Image, Text } from '@react-pdf/renderer';

import { getCommentaryTableHelperText } from '../../../redux/features/selectors/utils/getCommentaryTableHelperText.ts';
import { COMMENTARY_TEXT, PNG_HEIGHT, PNG_WIDTH } from '../../commentary/consts.ts';
import { ExportChartContainer } from '../../commentary/export/ExportChartContainer.tsx';
import { CommentarySection } from '../components/CommentarySection.tsx';
import { Table } from '../components/Table.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles } from '../styles.ts';
import { DateOfSaleAdjustmentsPageProps } from '../types.ts';

const ListToSaleAdjustmentSection = ({
  adjustmentSettings,
  adjustmentsTableData,
  compSequence,
  compTypes,
}: Omit<DateOfSaleAdjustmentsPageProps, 'tkv2ChartImages' | 'subjectAddress'>) => {
  const listToSaleTableData = adjustmentsTableData.listToSale;
  if (!listToSaleTableData) return null;

  const { commentary, method, ratio, tableData } = listToSaleTableData;
  if (method === 'none') return null;

  const { title, subtitle, headerText } = COMMENTARY_TEXT['list-to-sale'];
  const helperText = getCommentaryTableHelperText(adjustmentSettings, 'dateOfSale');

  return (
    <>
      <Text style={fontStyles.h4}>{title}</Text>
      <Text style={fontStyles.body}>{subtitle}</Text>
      <Text style={fontStyles.h5}>{headerText}</Text>

      <Table>
        <Table.Column>
          <Table.Cell compact />
          <Table.TextCell compact>DOM</Table.TextCell>
          <Table.TextCell compact>Original List Price</Table.TextCell>
          <Table.TextCell compact>Close Price</Table.TextCell>
          <Table.TextCell compact>SP as % LP</Table.TextCell>
        </Table.Column>
        {tableData.map((column) => {
          let compType: CompType | undefined;
          if (column.header !== 'Median') {
            const compNumber = Number(column.header.replace('Comp', '').trim());
            const compId = expected(compSequence[compNumber - 1]);
            compType = compTypes[compId];
          }

          return (
            <Table.Column key={randomUUID()}>
              <Table.CompTypeCell compact compType={compType}>
                {column.header}
              </Table.CompTypeCell>
              <Table.TextCell compact>{column.DOM}</Table.TextCell>
              <Table.TextCell compact>{column.originalListPrice}</Table.TextCell>
              <Table.TextCell compact>{column.closePrice}</Table.TextCell>
              <Table.TextCell compact>{column.salePriceAsPercentageOfListPrice}</Table.TextCell>
            </Table.Column>
          );
        })}
      </Table>

      <Text style={fontStyles.body}>
        List to sale price adjustment: {ratio ? `${ratio}%` : '-'}
      </Text>
      <Text style={fontStyles.small}>{helperText}</Text>
      <CommentarySection title="List to Sale Commentary" commentary={commentary} />
    </>
  );
};

const DateOfSaleAdjustmentSection = ({
  adjustmentSettings,
  adjustmentsTableData,
  tkv2ChartImages: { dateOfSaleChartImage },
}: Omit<DateOfSaleAdjustmentsPageProps, 'subjectAddress' | 'compTypes' | 'compSequence' | 'tkv2ChartImages'> & {
  tkv2ChartImages: Pick<DateOfSaleAdjustmentsPageProps['tkv2ChartImages'], 'dateOfSaleChartImage'>;
}) => {
  const marketAdjustmentsTableData = adjustmentsTableData.marketAdjustments;

  if (!marketAdjustmentsTableData || marketAdjustmentsTableData.method === 'none') return null;

  const { commentary, method } = marketAdjustmentsTableData;

  const { title, subtitle } = COMMENTARY_TEXT['date-of-sale-adjustments'];
  const helperText = getCommentaryTableHelperText(adjustmentSettings, 'dateOfSale');

  return (
    <>
      <Text style={fontStyles.h4}>{title}</Text>
      <Text style={fontStyles.body}>{subtitle}</Text>

      {dateOfSaleChartImage && (
        <ExportChartContainer
          title={method === 'zillow' ? 'Zillow Home Value Index' : 'Custom Home Value Index'}
        >
          <Image src={dateOfSaleChartImage} style={{ height: PNG_HEIGHT, width: PNG_WIDTH }} />
        </ExportChartContainer>
      )}

      {helperText && <Text style={fontStyles.small}>{helperText}</Text>}
      <CommentarySection title="Date of Sale Commentary" commentary={commentary} />
    </>
  );
};

export const DateOfSaleAdjustmentsPage = ({
  subjectAddress,
  adjustmentSettings,
  adjustmentsTableData,
  tkv2ChartImages: { dateOfSaleChartImage },
  compSequence,
  compTypes,
}: DateOfSaleAdjustmentsPageProps) => (
  <Tkv2PDFPage title="Date of Sale Adjustment" address={subjectAddress}>
    <DateOfSaleAdjustmentSection
      adjustmentSettings={adjustmentSettings}
      adjustmentsTableData={adjustmentsTableData}
      tkv2ChartImages={{ dateOfSaleChartImage }}
    />
    <ListToSaleAdjustmentSection
      adjustmentSettings={adjustmentSettings}
      adjustmentsTableData={adjustmentsTableData}
      compSequence={compSequence}
      compTypes={compTypes}
    />
  </Tkv2PDFPage>
);
